"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Info, Fuel, Calendar, Gauge, ArrowRight, CarFront, CircleAlert } from "lucide-react";
const wp_query_params = {
  post_type: "product",
  per_page: 6,
  status: "publish",
  orderby: "date",
  order: "desc"
};
const wp_query = new WP_Query(wp_query_params);
function InventoryGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  const filteredProducts = (products || []).filter(product => product.title !== "BMW 5 Serise" && product.title !== "Audi A7 50TFSI-S Line");
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[420px] w-full animate-pulse rounded-xl bg-muted/10" />)}
      </div>;
  }
  if (error) {
    return <div className="flex min-h-[300px] flex-col items-center justify-center text-center text-muted-foreground">
        <CircleAlert className="mb-4 h-10 w-10 opacity-50" />
        <p>Unable to load inventory at this time.</p>
        <Button variant="link" onClick={refetch} className="mt-2 text-primary">
          Try Again
        </Button>
      </div>;
  }
  if (filteredProducts.length === 0) {
    return <div className="flex min-h-[300px] flex-col items-center justify-center text-center text-muted-foreground">
        <CarFront className="mb-4 h-12 w-12 opacity-20" />
        <p>No vehicles currently available matching your criteria.</p>
      </div>;
  }
  const displayedProducts = filteredProducts.slice(3);
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {displayedProducts.map((product, i) => {
      const isSold = product.customFields?.stock_status === "outofstock";
      const badgeText = isSold ? "Sold" : "New Arrival";
      const badgeVariant = isSold ? "destructive" : "default";
      const mileage = product.customFields?.mileage || "24,000 km";
      const year = product.customFields?.year || "2023";
      const fuel = product.customFields?.fuel || "Hybrid";
      const transmission = product.customFields?.transmission || "Automatic";
      return <Card key={product.id} data-index={i} className="group relative overflow-hidden border-border/50 bg-card/50 transition-all duration-300 hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/10">
            {}
            <div className="absolute left-4 top-4 z-20">
              <Badge variant={badgeVariant} className="px-3 py-1 text-xs font-bold uppercase tracking-wider shadow-lg backdrop-blur-md">
                {badgeText}
              </Badge>
            </div>

            {}
            <div className="relative aspect-[16/10] overflow-hidden bg-muted/20">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted/10 text-muted-foreground">
                  <CarFront className="h-12 w-12 opacity-20" />
                </div>}
              
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent opacity-60" />
            </div>

            <CardHeader className="relative z-10 -mt-12 pb-2">
              <div className="flex items-end justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-xl font-bold text-foreground">
                    <Link to={product.link} className="hover:text-primary transition-colors">
                      {product.title}
                    </Link>
                  </CardTitle>
                  
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 pb-4">
              <div className="flex items-center justify-between border-t border-border/50 pt-4">
                <div className="text-sm text-muted-foreground">
                  {product.short_description ? <div className="line-clamp-2" dangerouslySetInnerHTML={{
                __html: product.short_description
              }} /> : <span>Premium vehicle ready for immediate delivery.</span>}
                </div>
              </div>

              {}
              <div className="flex items-center justify-between">
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-auto gap-2 px-0 text-xs font-medium text-muted-foreground hover:bg-transparent hover:text-primary">
                      <Info className="h-4 w-4" />
                      View Technical Specs
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 border-border/50 bg-popover/95 p-4 backdrop-blur-xl">
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-foreground">
                        Vehicle Specifications
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-2">
                          <Gauge className="h-4 w-4 text-primary" />
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase text-muted-foreground">
                              Mileage
                            </span>
                            <span className="text-xs font-medium text-foreground">
                              {mileage}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-2">
                          <SafeHydrate><Calendar className="h-4 w-4 text-primary" /></SafeHydrate>
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase text-muted-foreground">
                              Year
                            </span>
                            <span className="text-xs font-medium text-foreground">
                              {year}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-2">
                          <Fuel className="h-4 w-4 text-primary" />
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase text-muted-foreground">
                              Fuel
                            </span>
                            <span className="text-xs font-medium text-foreground">
                              {fuel}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 rounded-lg bg-muted/50 p-2">
                          <CarFront className="h-4 w-4 text-primary" />
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase text-muted-foreground">
                              Trans.
                            </span>
                            <span className="text-xs font-medium text-foreground">
                              {transmission}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>
            </CardContent>

            <CardFooter className="flex gap-3 pt-0">
              <Link to={product.link} className="flex-1">
                <Button className="w-full group-hover:bg-primary/90" variant="outline">
                  Asking for Quote
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <div className="w-[100px]">
                 <AddToCartButton productId={product.id} variant="secondary" text="Reserve" className="w-full" buttonClassName="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80" showQuantity={false} />
              </div>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function InventoryShowcase() {
  return <section id="inventory-showcase" className="dark bg-background text-foreground py-20 md:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl space-y-4">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
              B2B Car Rental Solutions for Your <span className="text-primary">Business</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Discover flexible vehicle rental solutions tailored to your unique requirements. 
              From short-term mobility to extended rental agreements, we provide premium 
              transportation options designed to keep you moving with confidence.
            </p>
          </div>
          <Link to="#">
            <Button variant="link" className="text-primary px-0 text-base">
              View Full Inventory <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <EcommerceProductsProvider wp_query={{
        post_type: "product",
        per_page: 6,
        status: ["publish"],
        orderby: "date",
        order: "desc"
      }} className="min-h-[400px] w-full">
          <InventoryGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}